import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import CardContainer from 'components/kea/CardContainer';
import SafeProductImage from 'components/kea/SafeProductImage';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import styles from './ProductDetail.module.scss';

const ProductThumbnail = ({ imageUrl, name }) => {
  const localizeMessage = useLocalizedMessage();
  return (
    <div className={styles.imageFrame}>
      <SafeProductImage
        src={imageUrl}
        alt={name || localizeMessage('catalog.product.fallbackName')}
        className={styles.productImage}
      />
    </div>
  );
};

ProductThumbnail.propTypes = {
  imageUrl: PropTypes.string,
  name: PropTypes.string,
};

ProductThumbnail.defaultProps = { imageUrl: '', name: '' };

const formatPrice = (value, currency) => (
  value === null || value === undefined || value === '' ? '—' : `${value} ${currency || ''}`.trim()
);

const ProductSection = ({ product }) => {
  const localizeMessage = useLocalizedMessage();
  const imageSource = product.picture || product.thumbnail;
  const description = product.description && product.description.length > 220
    ? `${product.description.slice(0, 217).trim()}…`
    : product.description;

  return (
    <div className={styles.layout}>
      <ProductThumbnail imageUrl={imageSource} name={product.name} />
      <div>
        <h2 className={styles.name}>{product.name || localizeMessage('catalog.product.fallbackName')}</h2>
        <div className={styles.identifiers}>
          {product.id && <span className={styles.badge}>{`ID ${product.id}`}</span>}
          {product.vendorCode && <span className={styles.badge}>{`SKU ${product.vendorCode}`}</span>}
          {product.vendor && <span className={styles.badge}>{product.vendor}</span>}
        </div>
        {description && <p className={styles.description}>{description}</p>}
        <div className={styles.priceRow}>
          <span className={styles.price}>{formatPrice(product.price, product.currencyId)}</span>
          {product.oldprice && <span className={styles.oldPrice}>{formatPrice(product.oldprice, product.currencyId)}</span>}
        </div>
      </div>
    </div>
  );
};

ProductSection.propTypes = { product: PropTypes.shape({}).isRequired };

const ExternalLink = ({ product }) => {
  const localizeMessage = useLocalizedMessage();
  const { settings: { shopAddress } } = useSelector((store) => store);
  const url = product?.data?.url;
  if (!url) return null;
  const normalizedShop = String(shopAddress || '').replace(/^https?:\/\//, '').replace(/\/$/, '');
  const href = /^https?:\/\//i.test(url) ? url : `https://${normalizedShop}/${String(url).replace(/^\//, '')}`;
  return <a className={styles.shopLink} target="_blank" rel="noopener noreferrer" href={href}>{localizeMessage('catalog.product.detail.openInStore')}</a>;
};

ExternalLink.propTypes = { product: PropTypes.shape({ data: PropTypes.shape({ url: PropTypes.string }) }).isRequired };

const ProductDetail = ({ product }) => (
  <CardContainer
    headerId="catalog.product.detail.fallbackTitle"
    actionComponent={<ExternalLink product={product} />}
    isFetching={product.isFetching}
  >
    {product.data && <ProductSection product={product.data} />}
  </CardContainer>
);

ProductDetail.propTypes = {
  product: PropTypes.shape({
    isFetching: PropTypes.bool,
    data: PropTypes.shape({}),
  }).isRequired,
};

export default ProductDetail;
